"use client"

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Check, Mail, UserPlus } from 'lucide-react'
import { trackConversion, getJourneyReport, getTrackingData } from '@/lib/user-tracking'
import { trackAdvancedConversion, getAdvancedTrackingData } from '@/lib/advanced-tracking'
import { usePathname } from 'next/navigation'

export function FloatingHelpButton() {
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const pathname = usePathname()
  
  // Don't show popup on the get-started page
  const isGetStartedPage = pathname === '/get-started'

  useEffect(() => {
    // Don't set up popup on get-started page
    if (isGetStartedPage) return
    
    const handleShowModal = () => {
      setShowQuoteModal(true)
    }
    
    window.addEventListener('showQuoteModal', handleShowModal)
    
    // Auto-show the modal after 20 seconds if not already shown
    const timer = setTimeout(() => {
      const hasSeenPopup = sessionStorage.getItem('buyersGuidePopupSeen')
      if (!hasSeenPopup) {
        setShowQuoteModal(true)
        sessionStorage.setItem('buyersGuidePopupSeen', 'true')
      }
    }, 20000) // 20 seconds
    
    return () => {
      window.removeEventListener('showQuoteModal', handleShowModal)
      clearTimeout(timer)
    }
  }, [isGetStartedPage])

  return (
    <>
      {/* Floating Help Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <a
          href="/get-started"
          className="bg-brand-orange hover:bg-brand-orange-hover text-white px-6 py-3 rounded-full shadow-xl inline-flex items-center gap-2 font-semibold transition-all duration-150 ease-in-out relative overflow-hidden animate-pulse-shadow"
          style={{ backgroundColor: 'var(--brand-orange)' }}
        >
          {/* Shine effect */}
          <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shine" />
          
          <svg className="w-5 h-5 relative z-10" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
            <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
          </svg>
          <span className="relative z-10">Questions?</span>
        </a>
      </div>

      {/* Get Buyer's Guide Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-8 relative max-h-[90vh] overflow-y-auto">
            {!isSuccess ? (
              <>
                <button
                  onClick={() => {
                    setShowQuoteModal(false)
                    setIsSuccess(false)
                  }}
                  className="absolute top-3 right-3 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 rounded-full p-2 z-10 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="flex items-center justify-center mb-4 mt-2">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#c88546]/10 rounded-full flex items-center justify-center">
                    <Mail className="w-7 h-7 sm:w-8 sm:h-8 text-[#c88546]" />
                  </div>
                </div>

                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-center">Get My Free Buyer's Guide</h3>
                <p className="text-gray-600 mb-4 sm:mb-6 text-center text-sm sm:text-base">Learn our 12-step process to homeownership - from site evaluation to move-in day</p>
                
                <div className="bg-[#c88546]/10 rounded-lg p-3 mb-4 text-center">
                  <p className="text-sm text-gray-700">Or call R.J. directly:</p>
                  <a href="tel:9283610298" className="text-lg font-bold text-[#c88546] hover:text-[#b87439]">(928) 361-0298</a>
                </div>
                
                <form onSubmit={async (e) => {
                  e.preventDefault()
                  setIsSubmitting(true)
                  const formData = new FormData(e.currentTarget)
                  
                  // Track conversion
                  trackConversion('Buyers Guide Popup')
                  trackAdvancedConversion('Buyers Guide Popup', ['name', 'email', 'phone', 'message'])
                  
                  // Get tracking data
                  const trackingData = getTrackingData()
                  const advancedTracking = await getAdvancedTrackingData()
                  const journeyReport = getJourneyReport()
                  
                  try {
                    const response = await fetch('/api/send-guide', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        name: formData.get('name'),
                        email: formData.get('email'),
                        phone: formData.get('phone'),
                        message: formData.get('message'),
                        tracking: trackingData,
                        advancedTracking: advancedTracking,
                        journeyReport: journeyReport
                      })
                    })
                    
                    if (response.ok) {
                      setIsSuccess(true)

                      // Fire Google Ads conversion tracking for Buyer's Guide with Enhanced Conversions
                      if (typeof window !== 'undefined' && (window as any).gtag) {
                        // Send user data for Enhanced Conversions (hashed automatically by gtag)
                        (window as any).gtag('set', 'user_data', {
                          'email': formData.get('email'),
                          'phone_number': formData.get('phone')
                        });

                        // Fire Google Ads conversion
                        (window as any).gtag('event', 'conversion', {
                          'send_to': 'AW-16837922212/6pcnCILE-5UbEKSb-dw-',
                          'value': 1.0,
                          'currency': 'USD'
                        });

                        // Also send to GA4 for tracking
                        (window as any).gtag('event', 'generate_lead', {
                          'currency': 'USD',
                          'value': 1.0,
                          'form_name': 'buyers_guide_popup'
                        });
                      }
                    }
                  } catch (error) {
                    console.error('Error:', error)
                    alert('There was an error. Please try again.')
                  } finally {
                    setIsSubmitting(false)
                  }
                }}>
                  <input
                    name="name"
                    type="text"
                    placeholder="Your name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-[#c88546] transition-all"
                    required
                    disabled={isSubmitting}
                  />
                  <input
                    name="email"
                    type="email"
                    placeholder="Your email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-[#c88546] transition-all"
                    required
                    disabled={isSubmitting}
                  />
                  <input
                    name="phone"
                    type="tel"
                    placeholder="Your phone number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-[#c88546] transition-all"
                    required
                    disabled={isSubmitting}
                  />
                  <textarea
                    name="message"
                    placeholder="Any questions or details about what you're looking for? (Optional)"
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-[#c88546] transition-all resize-none"
                    disabled={isSubmitting}
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-[#c88546] hover:bg-[#b87439] text-white py-3 transition-all"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      'Get My Free Guide'
                    )}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center py-4 sm:py-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <button
                  onClick={() => {
                    setShowQuoteModal(false)
                    setIsSuccess(false)
                  }}
                  className="absolute top-3 right-3 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 rounded-full p-2 z-10 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 animate-in zoom-in duration-500">
                  <Check className="w-8 h-8 sm:w-10 sm:h-10 text-white animate-in fade-in duration-700 delay-200" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3">Success!</h3>
                <p className="text-gray-600 mb-2 text-sm sm:text-base">Check your email for the buyer's guide.</p>
                <p className="text-sm text-gray-500 mb-6">We've sent your comprehensive 12-step guide to help you get started.</p>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Need help or have questions?</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    Call R.J. at <a href="tel:9283610298" className="text-[#c88546] font-semibold">(928) 361-0298</a>
                  </p>
                </div>
                
                <button
                  onClick={() => {
                    setShowQuoteModal(false)
                    setIsSuccess(false)
                  }}
                  className="mt-6 text-gray-500 hover:text-gray-700 text-sm underline"
                >
                  Close Window
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export function ShowMoreReviews({ children }: { children: React.ReactNode }) {
  const [showAllReviews, setShowAllReviews] = useState(false)

  return (
    <>
      <div className={showAllReviews ? 'block' : 'hidden'}>
        {children}
      </div>
      
      {!showAllReviews && (
        <div className="text-center mt-8">
          <Button 
            onClick={() => setShowAllReviews(true)}
            className="bg-[#c88546] hover:bg-[#b87439] text-white px-8 py-3 transition-all duration-150 ease-in-out shadow-lg hover:shadow-xl"
          >
            Show More Reviews
          </Button>
        </div>
      )}
    </>
  )
}